import { type ColumnDef } from '@tanstack/react-table';
import { Folder } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { Node } from '@/components/inspector/node';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface ParentColumnOptions<T> {
  /**
   * Unique identifier (falls back to accessorKey if omitted)
   *
   * @return {string} unique identifier
   */
  id?: string;

  accessorKey?: keyof T;
  accessorFn?: (row: T) => any;

  /**
   * Column title
   *
   * @return {string} column title string
   */
  label: string;

  /**
   * Gallery view (LIST or TILE)
   *
   * @return {GalleryView}
   */
  galleryView?: GalleryView;

  /**
   * isMainColumn property changes font's color and width to be more prominent
   *
   * @return {boolean}
   */
  isMainColumn?: boolean;

  /**
   * In the fixed-layout list the column gets no fixed width, so it absorbs
   * leftover space when the table is wider than the sum of column sizes
   *
   * @return {boolean}
   */
  flexWidth?: boolean;

  /**
   * Clamp vs wrap the cell’s content
   *
   * @return {boolean}
   */
  truncateValue?: boolean;

  /**
   * Show header with clamp + tooltip
   *
   * @return {boolean}
   */
  truncateHeaderValue?: boolean;

  /** Override for your main column in TILE (only if truncateValue)
   *
   * @return {string}
   */
  headerClassName?: string;

  /** Override your cell styles
   *
   * @return {string}
   */
  cellClassName?: string;

  /** Disable if you don't want the column to be hideable
   *
   * @return {boolean}
   */
  enableHiding?: boolean;

  /** Disable if you don't want the column to be sortable
   *
   * @return {boolean}
   */
  enableSorting?: boolean;

  /** Min width of the column
   *
   * @return {number}
   */
  minSize?: number;

  /** Max width of the column
   *
   * @return {number}
   */
  maxSize?: number;

  /** Width of the column
   *
   * @return {number}
   */
  size?: number;

  /** Data-cy attribute for testing
   *
   * @return {string}
   */
  dataCy?: string;
}

export const ParentColumn = <T extends Record<string, any>>(opts: ParentColumnOptions<T>): ColumnDef<T> => {
  const {
    id,
    accessorFn,
    accessorKey,
    label,
    galleryView,
    size,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    isMainColumn = false,
    flexWidth = false,
    truncateValue = true,
    truncateHeaderValue = false,
    minSize,
    maxSize,
    dataCy,
  } = opts;

  return {
    id,
    ...(accessorKey ? { accessorKey: String(accessorKey) } : {}),
    ...(accessorFn ? { accessorFn } : {}),
    enableSorting,
    size,
    minSize,
    maxSize,
    header: () =>
      truncateHeaderValue ? (
        <Tooltip>
          <TooltipTrigger className={cn({ 'line-clamp-1': truncateHeaderValue })}>{label}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all capitalize">{label}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : (
        label
      ),
    meta: {
      flexWidth,
      cellClassName: cn(cellClassName, {
        'text-left dark:text-white text-black font-semibold': isMainColumn,
        // Cap the list-view <td> width so an auto-layout table can't grow it to the
        // content's width. Without this, the inner line-clamp never clips and long
        // unbroken parent paths blow out the row instead of truncating. (DM-2393)
        'max-w-0': truncateValue,
      }),
      ...(headerClassName && { headerClassName: headerClassName }),
    },
    enableHiding,
    cell: ({ getValue, row }) => {
      const raw = getValue();

      if (raw === null) {
        return '';
      }

      const text = String(raw);

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <p
              className={cn({
                // `truncate` would set whitespace-nowrap, which inherits into the Node's
                // line-clamp-1 text and stops it from wrapping — the path gets hard-clipped
                // with no ellipsis. overflow-hidden bounds the cell and lets the Node's own
                // clamp produce the ellipsis. (DM-2393)
                'overflow-hidden': truncateValue,
                'break-all': !truncateValue,

                // override for your main column in TILE (only if truncateValue). min-w-0 lets
                // this grow flex item shrink below its content width so truncate can clip —
                // without it a long unbroken path overflows the tile row instead. (DM-2393)
                'truncate grow min-w-0 text-sm font-medium text-text-base':
                  isMainColumn && galleryView === GalleryView.TILE && truncateValue,

                // non-main columns always get gray
                'text-text-gray': !isMainColumn,
                // and they shrink in TILE
                'grow min-w-0 text-xs': !isMainColumn && galleryView === GalleryView.TILE,
              })}
              data-cy={dataCy ?? `${galleryView ?? 'list'}-${row.index}-${id}`}
            >
              <Node icon={Folder} iconClassName="stroke-folder-color" path={text} />
            </p>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all">{text}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      );
    },
  } as ColumnDef<T>;
};
